import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatCurrency } from '../../lib/utils';
import {
    Users, MapPin, Building2, Ticket, TrendingUp, TrendingDown, Headphones,
    ShoppingBag, Undo2, Tag, MessageCircle, ArrowRight, Loader2, BarChart3
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell } from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-[#1a1f2e] border border-white/10 rounded-lg px-3 py-2 shadow-xl">
            <p className="text-xs text-white/50 mb-1">{label}</p>
            {payload.map((p, i) => (
                <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
                    {p.name}: {typeof p.value === 'number' && p.name !== 'Orders' ? formatCurrency(p.value) : p.value}
                </p>
            ))}
        </div>
    );
};

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => { fetchStats(); }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${API}/api/admin/dashboard`, { withCredentials: true });
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <DashboardLayout type="admin">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-6 h-6 text-gold animate-spin" />
                </div>
            </DashboardLayout>
        );
    }

    const b2c = stats?.b2c || {};
    const monthly = stats?.monthly_revenue || [];
    const topExp = stats?.top_experiences || [];
    const recent = stats?.recent_orders || [];

    const statusColors = {
        paid: '#22c55e', refunded: '#ef4444', partially_refunded: '#f59e0b',
        pending: '#6366f1', failed: '#64748b'
    };

    const orderStatusData = [
        { name: 'Paid', value: b2c.paid || 0, color: statusColors.paid },
        { name: 'Refunded', value: b2c.refunded || 0, color: statusColors.refunded },
        { name: 'Partial', value: b2c.partially_refunded || 0, color: statusColors.partially_refunded },
        { name: 'Pending', value: b2c.pending || 0, color: statusColors.pending },
    ].filter(d => d.value > 0);

    const statusBadge = (s) => {
        const m = {
            paid: 'bg-emerald-500/15 text-emerald-400', refunded: 'bg-red-500/15 text-red-400',
            partially_refunded: 'bg-amber-500/15 text-amber-400', pending: 'bg-indigo-500/15 text-indigo-400',
            failed: 'bg-slate-500/15 text-slate-400',
        };
        return m[s] || 'bg-white/5 text-white/40';
    };

    return (
        <DashboardLayout type="admin">
            <div className="space-y-6" data-testid="admin-dashboard">
                <div>
                    <h1 className="font-outfit text-2xl lg:text-3xl font-bold text-white">Dashboard</h1>
                    <p className="text-muted-foreground mt-1">Platform performance & analytics</p>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-[#121620] border-white/10" data-testid="metric-net-revenue">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">Net Revenue</span>
                                <TrendingUp className="w-4 h-4 text-emerald-400" />
                            </div>
                            <p className="text-2xl font-bold text-white">{formatCurrency(b2c.net_revenue || 0)}</p>
                            <p className="text-[11px] text-white/30 mt-1">Gross {formatCurrency(b2c.gross_revenue || 0)} &middot; Refunds {formatCurrency(b2c.refunded_amount || 0)}</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#121620] border-white/10" data-testid="metric-orders">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">B2C Orders</span>
                                <ShoppingBag className="w-4 h-4 text-gold" />
                            </div>
                            <p className="text-2xl font-bold text-white">{b2c.total_orders || 0}</p>
                            <p className="text-[11px] text-white/30 mt-1">{b2c.paid || 0} paid &middot; {b2c.pending || 0} pending</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#121620] border-white/10" data-testid="metric-refund-rate">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">Refund Rate</span>
                                {(b2c.refund_rate || 0) > 10
                                    ? <TrendingDown className="w-4 h-4 text-red-400" />
                                    : <TrendingUp className="w-4 h-4 text-emerald-400" />
                                }
                            </div>
                            <p className={`text-2xl font-bold ${(b2c.refund_rate || 0) > 10 ? 'text-red-400' : 'text-emerald-400'}`}>{b2c.refund_rate || 0}%</p>
                            <p className="text-[11px] text-white/30 mt-1">{(b2c.refunded || 0) + (b2c.partially_refunded || 0)} refunded of {(b2c.paid || 0) + (b2c.refunded || 0) + (b2c.partially_refunded || 0)} completed</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-[#121620] border-white/10" data-testid="metric-platform">
                        <CardContent className="p-4">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-white/40 uppercase tracking-wider">Platform</span>
                                <BarChart3 className="w-4 h-4 text-blue-400" />
                            </div>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-1">
                                <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-orange-400" /><span className="text-sm text-white">{stats?.total_cities || 0}</span><span className="text-[10px] text-white/30">cities</span></div>
                                <div className="flex items-center gap-1.5"><Headphones className="w-3 h-3 text-pink-400" /><span className="text-sm text-white">{stats?.total_attractions || 0}</span><span className="text-[10px] text-white/30">attractions</span></div>
                                <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3 text-blue-400" /><span className="text-sm text-white">{stats?.total_operators || 0}</span><span className="text-[10px] text-white/30">operators</span></div>
                                <div className="flex items-center gap-1.5"><Users className="w-3 h-3 text-purple-400" /><span className="text-sm text-white">{stats?.total_travelers || 0}</span><span className="text-[10px] text-white/30">travelers</span></div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions */}
                <div className="flex gap-3 flex-wrap">
                    {stats?.escalated_chats > 0 && (
                        <button onClick={() => navigate('/admin/chat')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-400 text-xs font-medium hover:bg-amber-500/15 transition-colors" data-testid="alert-chats">
                            <MessageCircle className="w-3.5 h-3.5" /> {stats.escalated_chats} escalated chat{stats.escalated_chats !== 1 ? 's' : ''}
                        </button>
                    )}
                    {stats?.active_discounts > 0 && (
                        <button onClick={() => navigate('/admin/discounts')} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-medium hover:bg-emerald-500/15 transition-colors" data-testid="alert-discounts">
                            <Tag className="w-3.5 h-3.5" /> {stats.active_discounts} active discount code{stats.active_discounts !== 1 ? 's' : ''}
                        </button>
                    )}
                </div>

                {/* Charts Row */}
                <div className="grid lg:grid-cols-3 gap-5">
                    {/* Revenue Trend */}
                    <Card className="lg:col-span-2 bg-[#121620] border-white/10">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-white font-outfit text-base">Revenue Trend (6 months)</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="h-[280px]">
                                {monthly.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={monthly} barGap={4}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#1e2636" />
                                            <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
                                            <YAxis stroke="#64748b" fontSize={11} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                                            <Tooltip content={<CustomTooltip />} />
                                            <Bar dataKey="revenue" name="Revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                                            <Bar dataKey="refunds" name="Refunds" fill="#ef4444" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="flex items-center justify-center h-full text-white/20 text-sm">No revenue data yet</div>
                                )}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Order Status Distribution */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2">
                            <CardTitle className="text-white font-outfit text-base">Order Status</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {orderStatusData.length > 0 ? (
                                <>
                                    <div className="h-[180px]">
                                        <ResponsiveContainer width="100%" height="100%">
                                            <PieChart>
                                                <Pie data={orderStatusData} cx="50%" cy="50%" innerRadius={45} outerRadius={72} paddingAngle={3} dataKey="value">
                                                    {orderStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                                                </Pie>
                                                <Tooltip content={<CustomTooltip />} />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </div>
                                    <div className="space-y-2 mt-2">
                                        {orderStatusData.map(d => (
                                            <div key={d.name} className="flex items-center justify-between text-sm">
                                                <div className="flex items-center gap-2">
                                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: d.color }} />
                                                    <span className="text-white/50 text-xs">{d.name}</span>
                                                </div>
                                                <span className="text-white font-medium text-xs">{d.value}</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            ) : (
                                <div className="flex items-center justify-center h-[200px] text-white/20 text-sm">No orders yet</div>
                            )}
                        </CardContent>
                    </Card>
                </div>

                {/* Bottom Row */}
                <div className="grid lg:grid-cols-2 gap-5">
                    {/* Top Selling Experiences */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-white font-outfit text-base">Top Experiences</CardTitle>
                                <button onClick={() => navigate('/admin/listings')} className="text-[10px] text-gold hover:text-gold/80 flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {topExp.length > 0 ? (
                                <div className="space-y-3">
                                    {topExp.map((exp, i) => (
                                        <div key={exp._id || i} className="flex items-center gap-3 p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                                            <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center text-gold font-bold text-sm shrink-0">
                                                {i + 1}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-white truncate font-medium">{exp.title || 'Unknown'}</p>
                                                <p className="text-[11px] text-white/30">{exp.orders} order{exp.orders !== 1 ? 's' : ''}</p>
                                            </div>
                                            <span className="text-sm font-semibold text-gold">{formatCurrency(exp.revenue || 0)}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-8 text-center text-white/20 text-sm">No sales data yet</div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Recent Orders */}
                    <Card className="bg-[#121620] border-white/10">
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle className="text-white font-outfit text-base">Recent Orders</CardTitle>
                                <button onClick={() => navigate('/admin/b2c-orders')} className="text-[10px] text-gold hover:text-gold/80 flex items-center gap-1">View all <ArrowRight className="w-3 h-3" /></button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {recent.length > 0 ? (
                                <div className="space-y-2">
                                    {recent.slice(0, 6).map(order => (
                                        <div key={order.id} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.02] hover:bg-white/[0.04] transition-colors">
                                            <div className="flex items-center gap-3 min-w-0 flex-1">
                                                <div className="min-w-0">
                                                    <p className="text-xs text-white font-medium truncate">{order.customer_name || 'Guest'}</p>
                                                    <p className="text-[10px] text-white/30 truncate">{order.listing_title}</p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3 shrink-0">
                                                <span className="text-xs font-semibold text-white">{formatCurrency(order.total_amount || 0, order.currency?.toUpperCase() || 'EUR')}</span>
                                                <span className={`text-[10px] px-1.5 py-0.5 rounded ${statusBadge(order.payment_status)}`}>
                                                    {order.payment_status === 'partially_refunded' ? 'partial' : order.payment_status}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-8 text-center text-white/20 text-sm">No orders yet</div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
}
